import { useState, useEffect } from "react";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import { useDataStore } from "@store/user_org";
import { organizationsApi } from "@services/api/organizations";
import { showNotification } from "context/notification.service";

export function Payments() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const bridge = orgData?.bridge;

  const [iban, setIban] = useState(bridge?.iban || "");
  const [companyName, setCompanyName] = useState(bridge?.company_name || "");
  const [firstName, setFirstName] = useState(bridge?.first_name || "");
  const [lastName, setLastName] = useState(bridge?.last_name || "");
  const [email, setEmail] = useState(bridge?.email || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (bridge) {
      setIban(bridge.iban || "");
      setCompanyName(bridge.company_name || "");
      setFirstName(bridge.first_name || "");
      setLastName(bridge.last_name || "");
      setEmail(bridge.email || "");
    }
  }, [bridge]);

  const handleSave = async () => {
    if (!orgData) return;
    setSaving(true);
    try {
      await organizationsApi.update(orgData.id, {
        bridge: {
          iban,
          company_name: companyName,
          first_name: firstName,
          last_name: lastName,
          email,
          connected: !!iban,
        },
      } as any);
      await fetchOrgData(orgData.id);
      showNotification({ type: "success", message: "Configuration Bridge enregistrée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1
        className="text-hx font-semibold mb-2"
        style={{ fontFamily: "Poppins" }}
      >
        Paiements en ligne
      </h1>
      <p
        className="text-h2 text-neutral-500 mb-8"
        style={{ fontFamily: "Poppins" }}
      >
        Configurez vos informations bancaires pour recevoir des paiements par
        virement via Bridge. Vos clients recevront un lien de paiement sécurisé
        par email.
      </p>

      {/* Status badge */}
      {bridge?.connected ? (
        <div
          className="flex flex-row items-center gap-2 mb-6 p-3 rounded-lg"
          style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: "#22C55E",
            }}
          />
          <span
            className="text-h2 font-medium"
            style={{ fontFamily: "Poppins", color: "#166534" }}
          >
            Connecté — prêt à recevoir des paiements
          </span>
        </div>
      ) : (
        <div
          className="flex flex-row items-center gap-2 mb-6 p-3 rounded-lg"
          style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              background: "#F59E0B",
            }}
          />
          <span
            className="text-h2 font-medium"
            style={{ fontFamily: "Poppins", color: "#92400E" }}
          >
            Non configuré — renseignez vos informations ci-dessous
          </span>
        </div>
      )}

      {/* Form */}
      <div
        className="flex flex-col gap-4 p-6 rounded-xl"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <TextField label="IBAN" value={iban} onChange={setIban} />
        <TextField
          label="Nom de l'entreprise"
          value={companyName}
          onChange={setCompanyName}
        />
        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <TextField label="Prénom" value={firstName} onChange={setFirstName} />
          </div>
          <div className="flex-1">
            <TextField label="Nom" value={lastName} onChange={setLastName} />
          </div>
        </div>
        <TextField label="Email" value={email} onChange={setEmail} />

        {bridge?.fees !== undefined && (
          <div className="flex flex-row items-center gap-2">
            <span
              className="text-h3 font-medium"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Frais de transaction :
            </span>
            <span
              className="text-h2 font-semibold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              {bridge.fees}%
            </span>
          </div>
        )}

        <div className="flex flex-row justify-end mt-2">
          <Button
            label={saving ? "Enregistrement..." : "Enregistrer"}
            onClick={handleSave}
            disabled={saving}
          />
        </div>
      </div>

      {/* How it works */}
      <div
        className="flex flex-col gap-4 p-5 rounded-lg mt-8"
        style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
      >
        <span
          className="text-h2 font-semibold"
          style={{ fontFamily: "Poppins", color: "#374151" }}
        >
          Comment ça marche
        </span>
        <div className="flex flex-col gap-3">
          {[
            {
              step: "1",
              title: "Configuration",
              desc: "Renseignez l'IBAN de votre compte professionnel et vos coordonnées.",
            },
            {
              step: "2",
              title: "Demande de paiement",
              desc: "Depuis un ordre de réparation, demandez un acompte ou le solde. Le client reçoit un lien de paiement par email.",
            },
            {
              step: "3",
              title: "Paiement sécurisé",
              desc: "Le client effectue un virement bancaire instantané via Bridge (Bankin'). Aucune carte bancaire nécessaire.",
            },
            {
              step: "4",
              title: "Confirmation",
              desc: "Vous êtes notifié dès que le paiement est reçu. Le suivi client est mis à jour automatiquement.",
            },
          ].map((item) => (
            <div key={item.step} className="flex flex-row gap-3">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#3B82F6",
                  color: "#FFFFFF",
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {item.step}
              </div>
              <div className="flex flex-col gap-0.5">
                <span
                  className="text-h3 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#374151" }}
                >
                  {item.title}
                </span>
                <span
                  className="text-h4"
                  style={{ fontFamily: "Poppins", color: "#6B7280" }}
                >
                  {item.desc}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
